import { useModals } from '@any-ui-react/modals';
import classNames from 'classnames';
import { type ReactNode, useCallback, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { ActionMaterialIcon } from '@/common/components/shared/action-material-icon/ActionMaterialIcon';
import { HoverCardWithIcon } from '@/common/components/shared/hover-card/HoverCardWithIcon';
import type { ListEmployeesFilterInput } from '@/common/graphql';

import { EmployeeSearchForm } from './EmployeeSearchForm';
import type { EmployeeSearchResultRow } from './EmployeeSearchTable';
import type { EmployeeSearchProps } from './props';

interface Props extends EmployeeSearchProps {
  baseVariables: ListEmployeesFilterInput;
  mode: 'single' | 'multi';
  /** Currently-selected id(s) to seed/pre-check in the popup - a single id string in `mode:
   * 'single'`, a string array in `mode: 'multi'` (empty/null/undefined when nothing is selected). */
  value: string | string[] | null | undefined;
  /**
   * Called with the confirmed selection - a single row in `mode: 'single'` (row click confirms immediately),
   * an array of rows in `mode: 'multi'` (confirmed via the "Select" button).
   */
  onConfirm: (selected: EmployeeSearchResultRow | EmployeeSearchResultRow[]) => void;
  disabled?: boolean;
  /** Custom trigger content. Defaults to the search-icon + hover-card trigger when omitted. */
  children?: ReactNode;
  /** Only used together with `children`, applied to the trigger's wrapping element. */
  className?: string;
}

export const EmployeeSearchPopup = ({
  mode,
  baseVariables,
  disabled,
  sort,
  value,
  onConfirm,
  children,
  className,
  defaultDepartmentName,
  defaultCompanyId,
  defaultCountryId,
  searchFilters,
}: Props) => {
  const modalIdRef = useRef(uuid());
  const modalId = modalIdRef.current;
  const { openModal, closeModal } = useModals();

  const title = useMemo(() => (mode === 'single' ? 'Search an Employee' : 'Search Employees'), [mode]);
  const onClick = useCallback(() => {
    openModal({
      modalId,
      closeOnClickOutside: false,
      closeOnEscape: false,
      centered: true,
      title,
      size: 1024,
      children: (
        <EmployeeSearchForm
          baseVariables={baseVariables}
          defaultCompanyId={defaultCompanyId}
          defaultCountryId={defaultCountryId}
          defaultDepartmentName={defaultDepartmentName}
          mode={mode}
          searchFilters={searchFilters}
          sort={sort}
          value={value}
          onCancel={() => closeModal(modalId)}
          onConfirm={selected => {
            onConfirm(selected);
            closeModal(modalId);
          }}
        />
      ),
    });
  }, [
    baseVariables,
    closeModal,
    defaultCompanyId,
    defaultCountryId,
    defaultDepartmentName,
    modalId,
    mode,
    onConfirm,
    openModal,
    value,
    sort,
    title,
    searchFilters,
  ]);

  if (children) {
    return (
      <span className={className} onClick={disabled ? undefined : onClick}>
        {children}
      </span>
    );
  }

  return (
    <HoverCardWithIcon
      disabled={disabled}
      position="bottom-start"
      customTarget={
        <ActionMaterialIcon
          iconName="search"
          fontSize="14px"
          iconClassName={classNames({ 'text-blue-6': !disabled })}
          disabled={disabled}
          onClick={onClick}
        />
      }
    >
      {title}
    </HoverCardWithIcon>
  );
};
